import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "../../../components/common/SnackbarProvider";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import type { PreCallBriefing, ProspectInfo } from "../../../api/generated";

interface BriefingPanelProps {
  open: boolean;
  onClose: () => void;
  briefing: PreCallBriefing | null;
  prospect?: ProspectInfo | null;
}

function briefingToMarkdown(briefing: PreCallBriefing): string {
  const sections: string[] = [];

  if (briefing.company_overview) {
    sections.push(`## Company Overview\n${briefing.company_overview}`);
  }
  if (briefing.prospect_profile) {
    sections.push(`## Prospect Profile\n${briefing.prospect_profile}`);
  }
  if (briefing.pain_points?.length) {
    sections.push(
      `## Pain Points\n${briefing.pain_points.map((p) => `- ${p}`).join("\n")}`,
    );
  }
  if (briefing.discovery_questions?.length) {
    sections.push(
      `## Discovery Questions\n${briefing.discovery_questions.map((q, i) => `${i + 1}. ${q}`).join("\n")}`,
    );
  }
  if (briefing.competitive_landscape) {
    sections.push(
      `## Competitive Landscape\n${briefing.competitive_landscape}`,
    );
  }
  if (briefing.key_talking_points?.length) {
    sections.push(
      `## Key Talking Points\n${briefing.key_talking_points.map((p) => `- ${p}`).join("\n")}`,
    );
  }

  return sections.join("\n\n");
}

function briefingToHtml(
  briefing: PreCallBriefing,
  prospect?: ProspectInfo | null,
  primaryColor = "#0f172a",
): string {
  const header = [prospect?.name, prospect?.company]
    .filter(Boolean)
    .join(" \u2013 ");
  const date = new Date().toLocaleDateString();

  const section = (title: string, body: string) =>
    `<h2 style="color:${primaryColor};border-bottom:1px solid #e0e0e0;padding-bottom:4px">${title}</h2>${body}`;

  const parts: string[] = [];

  if (briefing.company_overview) {
    parts.push(
      section("Company Overview", `<p>${briefing.company_overview}</p>`),
    );
  }
  if (briefing.prospect_profile) {
    parts.push(
      section("Prospect Profile", `<p>${briefing.prospect_profile}</p>`),
    );
  }
  if (briefing.pain_points?.length) {
    const items = briefing.pain_points.map((p) => `<li>${p}</li>`).join("");
    parts.push(section("Potential Pain Points", `<ul>${items}</ul>`));
  }
  if (briefing.discovery_questions?.length) {
    const items = briefing.discovery_questions
      .map((q) => `<li>${q}</li>`)
      .join("");
    parts.push(section("Discovery Questions", `<ol>${items}</ol>`));
  }
  if (briefing.competitive_landscape) {
    parts.push(
      section(
        "Competitive Landscape",
        `<p>${briefing.competitive_landscape}</p>`,
      ),
    );
  }
  if (briefing.key_talking_points?.length) {
    const items = briefing.key_talking_points
      .map((p) => `<li>${p}</li>`)
      .join("");
    parts.push(section("Key Talking Points", `<ul>${items}</ul>`));
  }

  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Pre-Call Briefing${header ? ` \u2014 ${header}` : ""}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 700px; margin: 40px auto; padding: 0 20px; color: #333; line-height: 1.6; }
  h1 { font-size: 1.5em; margin-bottom: 4px; }
  h2 { font-size: 1.1em; margin-top: 24px; margin-bottom: 8px; }
  .meta { color: #888; font-size: 0.85em; margin-bottom: 24px; }
  ul, ol { padding-left: 20px; }
  li { margin-bottom: 4px; }
  @media print { body { margin: 20px; } }
</style></head><body>
<h1>Pre-Call Briefing</h1>
${header ? `<div class="meta">${header} &middot; ${date}</div>` : `<div class="meta">${date}</div>`}
${parts.join("")}
</body></html>`;
}

function hasContent(briefing: PreCallBriefing): boolean {
  return !!(
    briefing.company_overview ||
    briefing.prospect_profile ||
    (briefing.pain_points && briefing.pain_points.length > 0) ||
    (briefing.discovery_questions && briefing.discovery_questions.length > 0) ||
    briefing.competitive_landscape ||
    (briefing.key_talking_points && briefing.key_talking_points.length > 0)
  );
}

function BriefingSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export function BriefingPanel({
  open,
  onClose,
  briefing,
  prospect,
}: BriefingPanelProps) {
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const handleCopy = async () => {
    if (!briefing) return;
    const markdown = briefingToMarkdown(briefing);
    await navigator.clipboard.writeText(markdown);
    showSnackbar("Copied to clipboard");
  };

  const handleExportPdf = () => {
    if (!briefing) return;
    const html = briefingToHtml(briefing, prospect, theme.palette.primary.main);
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.addEventListener("afterprint", () => win.close());
    // Small delay to let styles render before print dialog
    setTimeout(() => win.print(), 250);
  };

  const showContent = briefing && hasContent(briefing);

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{ paper: { sx: { width: 400, p: 3 } } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6">Pre-Call Briefing</Typography>
          <Box>
            {showContent && (
              <>
                <Tooltip title="Export as PDF">
                  <IconButton
                    size="small"
                    onClick={handleExportPdf}
                    sx={{ mr: 0.5 }}
                  >
                    <PictureAsPdfIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Copy as markdown">
                  <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{ mr: 0.5 }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {!showContent ? (
          <Typography sx={{
            color: "text.secondary"
          }}>
            Briefing will appear after research is complete.
          </Typography>
        ) : (
          <>
            {briefing.company_overview && (
              <BriefingSection title="Company Overview">
                <Typography variant="body2">
                  {briefing.company_overview}
                </Typography>
              </BriefingSection>
            )}

            {briefing.prospect_profile && (
              <>
                <Divider sx={{ mb: 3 }} />
                <BriefingSection title="Prospect Profile">
                  <Typography variant="body2">
                    {briefing.prospect_profile}
                  </Typography>
                </BriefingSection>
              </>
            )}

            {briefing.pain_points && briefing.pain_points.length > 0 && (
              <>
                <Divider sx={{ mb: 3 }} />
                <BriefingSection title="Potential Pain Points">
                  {briefing.pain_points.map((point, idx) => (
                    <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                      &bull; {point}
                    </Typography>
                  ))}
                </BriefingSection>
              </>
            )}

            {briefing.discovery_questions &&
              briefing.discovery_questions.length > 0 && (
                <>
                  <Divider sx={{ mb: 3 }} />
                  <BriefingSection title="Discovery Questions">
                    {briefing.discovery_questions.map((question, idx) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                        {idx + 1}. {question}
                      </Typography>
                    ))}
                  </BriefingSection>
                </>
              )}

            {briefing.competitive_landscape && (
              <>
                <Divider sx={{ mb: 3 }} />
                <BriefingSection title="Competitive Landscape">
                  <Typography variant="body2">
                    {briefing.competitive_landscape}
                  </Typography>
                </BriefingSection>
              </>
            )}

            {briefing.key_talking_points &&
              briefing.key_talking_points.length > 0 && (
                <>
                  <Divider sx={{ mb: 3 }} />
                  <BriefingSection title="Key Talking Points">
                    {briefing.key_talking_points.map((point, idx) => (
                      <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                        &bull; {point}
                      </Typography>
                    ))}
                  </BriefingSection>
                </>
              )}
          </>
        )}
      </Drawer>
    </>
  );
}
